import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { authApi } from "../api/authApi";
import {
  resetCsrfToken,
  setUnauthenticatedHandler,
  toClientApiError,
} from "../api/httpClient";
import type {
  ActiveTenant,
  AuthUser,
  ClientApiError,
  Membership,
  SessionPayload,
} from "../api/types";
import {
  activeMemberships,
  deriveAuthStatus,
  type AuthSnapshot,
  type AuthStatus,
} from "./authState";

export type AuthContextValue = AuthSnapshot & {
  login: (email: string, password: string) => Promise<AuthSnapshot>;
  logout: () => Promise<void>;
  selectTenant: (tenantId: string) => Promise<AuthSnapshot>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const emptySnapshot = (): AuthSnapshot => ({
  status: "loading",
  user: null,
  memberships: [],
  activeTenant: null,
});

function normalizeAuthUser(user: AuthUser): AuthUser {
  return {
    ...user,
    platformRoles: user.platformRoles ?? [],
  };
}

function snapshotFromSession(
  user: AuthUser,
  memberships: Membership[],
  activeTenant: ActiveTenant | null,
): AuthSnapshot {
  const normalized = normalizeAuthUser(user);
  return {
    status: deriveAuthStatus({ user: normalized, activeTenant }),
    user: normalized,
    memberships,
    activeTenant,
  };
}

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [snapshot, setSnapshot] = useState<AuthSnapshot>(emptySnapshot);
  const autoSelectInFlight = useRef(false);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  const clearSession = useCallback(() => {
    resetCsrfToken();
    setSnapshot({
      status: "unauthenticated",
      user: null,
      memberships: [],
      activeTenant: null,
    });
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      clearSession();
    });
    return () => setUnauthenticatedHandler(null);
  }, [clearSession]);

  const applySession = useCallback((payload: SessionPayload | {
    user: AuthUser;
    memberships: Membership[];
    activeTenant: ActiveTenant | null;
  }) => {
    setSnapshot(
      snapshotFromSession(
        payload.user,
        payload.memberships,
        payload.activeTenant,
      ),
    );
  }, []);

  const tryAutoSelect = useCallback(async (
    user: AuthUser,
    memberships: Membership[],
    activeTenant: ActiveTenant | null,
  ) => {
    if (activeTenant || autoSelectInFlight.current) {
      return;
    }
    const active = activeMemberships(memberships);
    if (active.length !== 1) {
      return;
    }
    const only = active[0];
    if (!only) {
      return;
    }
    autoSelectInFlight.current = true;
    try {
      const result = await authApi.selectTenant(only.tenantId);
      setSnapshot(snapshotFromSession(user, memberships, result.activeTenant));
    } catch {
      setSnapshot(snapshotFromSession(user, memberships, null));
    } finally {
      autoSelectInFlight.current = false;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      applySession(me);
      await tryAutoSelect(me.user, me.memberships, me.activeTenant);
    } catch (error) {
      const apiError = toClientApiError(error);
      if (apiError.status === 401) {
        clearSession();
        return;
      }
      clearSession();
    }
  }, [applySession, clearSession, tryAutoSelect]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (cancelled) {
          return;
        }
        applySession(me);
        await tryAutoSelect(me.user, me.memberships, me.activeTenant);
      } catch {
        if (!cancelled) {
          clearSession();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession, tryAutoSelect]);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthSnapshot> => {
      const payload = await authApi.login(email, password);
      let next = snapshotFromSession(
        payload.user,
        payload.memberships,
        payload.activeTenant,
      );
      setSnapshot(next);

      if (!next.activeTenant) {
        const active = activeMemberships(next.memberships);
        if (active.length === 1 && active[0]) {
          try {
            const result = await authApi.selectTenant(active[0].tenantId);
            next = snapshotFromSession(
              payload.user,
              payload.memberships,
              result.activeTenant,
            );
            setSnapshot(next);
          } catch {
            // keep without tenant
          }
        }
      }
      return next;
    },
    [],
  );

  const selectTenant = useCallback(
    async (tenantId: string): Promise<AuthSnapshot> => {
      const current = snapshotRef.current;
      if (!current.user) {
        throw Object.assign(new Error("UNAUTHENTICATED"), {
          code: "UNAUTHENTICATED",
        });
      }
      const allowed = activeMemberships(current.memberships).some(
        (m) => m.tenantId === tenantId,
      );
      if (!allowed) {
        const err: ClientApiError = {
          status: 403,
          code: "TENANT_MEMBERSHIP_MISSING",
          message: "אין הרשאה לבחור ארגון זה",
          isNetworkError: false,
        };
        throw err;
      }
      const result = await authApi.selectTenant(tenantId);
      const next = snapshotFromSession(
        current.user,
        current.memberships,
        result.activeTenant,
      );
      setSnapshot(next);
      return next;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Session may already be expired — still clear client state.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...snapshot,
      login,
      logout,
      selectTenant,
      refresh,
    }),
    [snapshot, login, logout, selectTenant, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function useAuthStatus(): AuthStatus {
  return useAuth().status;
}
